export interface Book {
  studyUIDs: string[];
  pages: BookPage[]
  bookType: BookType;
  studyInfo: StudyInfo[];
  advancedVisor: boolean;
  //reports: ReportInfo[];
  activatedAI: boolean;
}

export interface BookPage {
  type: BookPageType;
  bookImages: BookImage[];
}

export namespace BookPage {
  export function sanitize(bp: BookPage): BookPage {
    return {
      type: BookPageType.sanitize(bp.type),
      bookImages: BookImage.sanitizeAll(bp.bookImages)
    }
  }

  export function sanitizeAll(bps: BookPage[]): BookPage[] {
    return bps.map(bp => sanitize(bp))
  }
}

export interface BookImage {
  type: BookImageType;
  pngFile: string[];
  mp4File: string | null;
  isKo: boolean;
  width: number;
  height: number;
  dicomFile?: string;
  sopInstanceUID?: string;
  seriesUID?: string;
  imageModifications?: any;
  prFiles?: string[]
  gridSettings?: GridSettings;
  description?: string;
}

export namespace BookImage {
  export function sanitize(bi: BookImage): BookImage {
    return {
      height: +bi.height,
      width: +bi.width,
      isKo: bi.isKo,
      pngFile: JSON.parse(JSON.stringify(bi.pngFile)),
      mp4File: bi.mp4File,
      type: BookImageType.sanitize(bi.type),
      description: bi.description,
      dicomFile: bi.dicomFile,
      gridSettings: bi.gridSettings,
      imageModifications: bi.imageModifications,
      prFiles: JSON.parse(JSON.stringify(bi.prFiles)),
      seriesUID: bi.seriesUID,
      sopInstanceUID: bi.sopInstanceUID
    }
  }

  export function sanitizeAll(bis: BookImage[]): BookImage[] {
    return bis.map(bi => sanitize(bi))
  }
}

export interface StudyInfo {
  serie: string;
  files: string[];
}

export namespace StudyInfo {
  export function sanitize(si: StudyInfo): StudyInfo {
    if(si==null) return {
      serie: '',
      files: []
    };
    return {
      serie: si.serie,
      files: JSON.parse(JSON.stringify(si.files))
    }
  }

  export function sanitizeAll(sis: StudyInfo[]): StudyInfo[] {
    if(sis==null) return [];
    return sis.map(si => sanitize(si))
  }
}

export interface GridSettings {
  grid: {
    rows: number,
    columns: number
  };
  dicomFiles: string[];
}

export interface ReportInfo {
  studyUID: string;
  pngs: string[];
}

export namespace ReportInfo {
  export function sanitize(ri: ReportInfo): ReportInfo {
    return {
      studyUID: ri.studyUID,
      pngs: JSON.parse(JSON.stringify(ri.pngs))
    }
  }

  export function sanitizeAll(ris: ReportInfo[]): ReportInfo[] {
    return ris.map(ri => sanitize(ri))
  }
}

export enum BookType {
  STATIC,
  DYNAMIC,
  OTHER
}

export enum BookPageType {
  REPORT,
  FILLER,
  COVER,
  IMAGES
}

export enum BookImageType {
  UNIQUE,
  SERIES,
  CINE,
  GRID
}

export enum BookStatus {
  NOT_READY,
  READY,
  EXPIRED,
  DELETED
}

export namespace Book {
  export function sanitize(b: Book): Book {

    return {
      advancedVisor: b.advancedVisor,
      bookType: BookType.sanitize(b.bookType),
      pages: BookPage.sanitizeAll(b.pages),
      //reports: ReportInfo.sanitizeAll(b.reports),
      studyInfo: StudyInfo.sanitizeAll(b.studyInfo),
      studyUIDs: JSON.parse(JSON.stringify(b.studyUIDs)),
      activatedAI: b.activatedAI
    }

    // b.advancedVisor = (b.advancedVisor as any == 'true' || b.advancedVisor == true) ? true : false;
    // b.bookType = BookType.sanitize(b.bookType);
    // b.pages.forEach(p => {
    //   p.type = BookPageType.sanitize(p.type);
    //   p.bookImages.forEach(img => {
    //     img.type = BookImageType.sanitize(img.type);
    //     img.isKo = sanitizeBoolean(img.isKo);
    //   })
    // })
    // b.reports = (b.reports != null) ? b.reports : [];
    // return b;
  }
}

export namespace BookType {
  export function sanitize(t: BookType | string | number): BookType {
    switch (t) {
      case 'STATIC':
      case '\"STATIC\"':
      case 0:
      case BookType.STATIC:
        return BookType.STATIC;
      case 'DYNAMIC':
      case '\"DYNAMIC\"':
      case 1:
      case BookType.DYNAMIC:
        return BookType.DYNAMIC;
      case 'OTHER':
      case '\"OTHER\"':
      case 2:
      case BookType.OTHER:
        return BookType.OTHER;
      default:
        return BookType.STATIC;
    }
  }
}

export namespace BookStatus {
  export function sanitize(t: BookStatus | string): BookStatus {
    switch (t) {
      case 'NOT_READY':
      case '\"NOT_READY\"':
      case BookStatus.NOT_READY:
        return BookStatus.NOT_READY;
      case 'READY':
      case '\"READY\"':
      case BookStatus.READY:
        return BookStatus.READY;
      case 'EXPIRED':
      case '\"EXPIRED\"':
      case BookStatus.EXPIRED:
        return BookStatus.EXPIRED;
      case 'DELETED':
      case '\"DELETED\"':
      case BookStatus.DELETED:
        return BookStatus.DELETED;
    }
    return BookStatus.NOT_READY;
  }
}

export namespace BookPageType {
  export function sanitize(t: BookPageType | string | number): BookPageType {
    switch (t) {
      case 'REPORT':
      case '\"REPORT\"':
      case BookPageType.REPORT:
      case 0:
        return BookPageType.REPORT;
      case 'COVER':
      case '\"COVER\"':
      case BookPageType.COVER:
      case 1:
        return BookPageType.COVER;
      case 'FILLER':
      case '\"FILLER\"':
      case BookPageType.FILLER:
      case 2:
        return BookPageType.FILLER;
      case 'IMAGES':
      case '\"IMAGES\"':
      case 'DICOM':
      case '\"DICOM\"':
      case 3:
      case BookPageType.IMAGES:
        return BookPageType.IMAGES;
    }
    return BookPageType.COVER;
  }
}

export namespace BookImageType {
  export function sanitize(t: BookImageType | string | number): BookImageType {
    const isPageSmall = (window.innerWidth <= 991);
    switch (t) {
      case 'UNIQUE':
      case '\"UNIQUE\"':
      case BookImageType.UNIQUE:
        return BookImageType.UNIQUE;
      case 'SERIES':
      case '\"SERIES\"':
      case BookImageType.SERIES:
        return BookImageType.SERIES;
      case 'CINE':
      case '\"CINE\"':
      case BookImageType.CINE:
        return (isPageSmall) ? BookImageType.SERIES : BookImageType.CINE;
      case 'GRID':
      case '\"GRID\"':
      case BookImageType.GRID:
        return (isPageSmall) ? BookImageType.SERIES : BookImageType.GRID;
      default:
        return BookImageType.UNIQUE;
    }
  }
}

export function sanitizeBoolean(b: boolean | string | number) {
  // @ts-ignore
  if (b == null || b == false || b == 'false' || b <= 0) return false;
  // @ts-ignore
  if (b == true || b == 'true' || b > 0) return true;
  return false;
}